import { useEffect, useState } from 'react';
import { DataSnapshot } from 'firebase/database';
import { getFirebaseRef } from 'services';

interface IUseFirebaseOptions {
  onInsert?(data: any): void;
}

type FirebaseState = {
  items: any[];
  error: boolean | Error;
};

const initialState: FirebaseState = {
  items: [],
  error: false,
};

const useFirebase = (query: string, options?: IUseFirebaseOptions) => {
  const [state, setState] = useState<FirebaseState>(initialState);

  const handleError = (error: Error) => setState((s) => ({ ...s, error }));

  const handleAdded = (snapshot: DataSnapshot, previousKey?: string | null) => {
    const data = snapshot.val();
    if (options && typeof options.onInsert === 'function') {
      options.onInsert(data);
    }
    setState((s) => ({
      ...s,
      items: [data, ...s.items],
      error: false,
    }));
  };

  useEffect(() => {
    if (!query) return;

    let isMounted = true;
    let childAddedHandler: any;
    let qRef: any;

    // Use an async IIFE to handle async/await in useEffect
    (async () => {
      // Dynamically import Firebase database functions
      const { onChildAdded, onValue, off } = await import('firebase/database');
      qRef = await getFirebaseRef(query);

      // We want to only listen to new notifications
      const onInitialLoad = (snapshot: DataSnapshot) => {
        if (!isMounted) return;
        const snapshotVal = snapshot.val();

        let childrenToProcess = snapshotVal
          ? Object.keys(snapshot.val()).length
          : 0;

        if (childrenToProcess === 0) {
          childAddedHandler = onChildAdded(qRef, handleAdded, handleError);
        } else {
          const children: DataSnapshot[] = [];

          const onChildAddedWithoutInitialLoad = (
            addedChild: DataSnapshot,
            previousKey?: string | null,
          ) => {
            // Ignore children from initial load
            if (childrenToProcess > 0) {
              childrenToProcess--;
              children.push(addedChild);
              return;
            }

            handleAdded(addedChild, previousKey);
          };

          childAddedHandler = onChildAdded(
            qRef,
            onChildAddedWithoutInitialLoad,
            handleError,
          );
        }
      };

      // Use a local function instead of R.F (Ramda's alwaysFalse)
      onValue(qRef, onInitialLoad, () => false, { onlyOnce: true });
    })().catch((err) => {
      // e.g. the Firebase sign-in behind getFirebaseRef failed
      if (isMounted) handleError(err);
    });

    // Remove listeners on unmount
    return () => {
      isMounted = false;
      if (qRef && childAddedHandler) {
        import('firebase/database').then(({ off }) => {
          off(qRef, 'child_added', childAddedHandler);
        });
      }
    };
  }, [query]);

  return state;
};

export default useFirebase;
